/**
 * The wallet screens' words and rules, kept pure so they can be tested
 * without rendering anything. Wording matches the web parent portal
 * (MyWalletPage / PayAndTopUpPage) so a family reads the same sentences on
 * both.
 */

import type { PayAndTopUp, PayChild, PayItem, Wallet, WalletEntry } from '../types/wallet';
import { parseBackendDate, parseBackendDateOnly } from './dateUtils';

/** A wallet's money in its own currency, always with cents: it is a balance. */
export function walletMoney(n: number | null | undefined, currency = 'USD'): string {
  if (n === null || n === undefined || Number.isNaN(n)) { return '—'; }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}

const KIND_LABEL: Record<string, string> = {
  TOPUP_COUNTER: 'Top-up at the counter',
  TOPUP_ONLINE: 'Top-up online',
  CHARGE: 'Spent',
  BILL_CLEARED: 'Paid an old bill',
  REFUND: 'Refund',
  REVERSAL: 'Reversal',
  ADJUSTMENT: 'Correction',
  GIFT: 'Gift points',
  POINTS_EXPIRY: 'Points expired',
  CLOSE_OUT: 'Paid out on closing',
  POINTS_CONVERTED: 'Points converted to money',
};
export const walletKindLabel = (kind: string) => KIND_LABEL[kind] ?? kind;

export interface Chip { label: string; fg: string; bg: string; darkFg: string; darkBg: string }

const chip = (label: string, fg: string, bg: string, darkFg: string, darkBg: string): Chip =>
  ({ label, fg, bg, darkFg, darkBg });

/** The wallet's state in one small chip: frozen/closed first, then low, else active. */
export function walletStatusChip(wallet: Pick<Wallet, 'status' | 'isLow'>): Chip {
  if (wallet.status === 'Frozen') { return chip('Frozen', '#1E40AF', '#DBEAFE', '#93C5FD', '#1E3A5F'); }
  if (wallet.status === 'Closed') { return chip('Closed', '#374151', '#F3F4F6', '#CBD5E1', '#334155'); }
  if (wallet.isLow) { return chip('Low balance', '#92400E', '#FEF3C7', '#FBBF24', '#4B3B0F'); }
  return chip('Active', '#065F46', '#D1FAE5', '#34D399', '#134E3A');
}

const ORDER_STATUS: Record<string, Chip> = {
  PENDING: chip('Pending', '#92400E', '#FEF3C7', '#FBBF24', '#4B3B0F'),
  ACCEPTED: chip('Accepted', '#1E40AF', '#DBEAFE', '#93C5FD', '#1E3A5F'),
  PREPARING: chip('Preparing', '#6D28D9', '#EDE9FE', '#C4B5FD', '#3B2A63'),
  READY: chip('Ready', '#065F46', '#D1FAE5', '#34D399', '#134E3A'),
  DELIVERED: chip('Delivered', '#374151', '#E5E7EB', '#CBD5E1', '#334155'),
  REJECTED: chip('Rejected', '#991B1B', '#FEE2E2', '#F87171', '#4C1D1D'),
  CANCELLED: chip('Cancelled', '#6B7280', '#F3F4F6', '#94A3B8', '#293548'),
};

/** A canteen order's status chip; an unknown status shows as itself in grey. */
export function orderStatusChip(status: string): Chip {
  const key = (status ?? '').toUpperCase();
  return ORDER_STATUS[key] ?? chip(status || 'Unknown', '#6B7280', '#F3F4F6', '#94A3B8', '#293548');
}

/** The sentence under a frozen or closed wallet, or null when it is active. */
export function walletStatusNotice(wallet: Pick<Wallet, 'status' | 'statusReason'>): string | null {
  const reason = wallet.statusReason ? ` (${wallet.statusReason})` : '';
  if (wallet.status === 'Frozen') {
    return `This wallet is frozen${reason}. Nothing can be spent from it until the school office unfreezes it.`;
  }
  if (wallet.status === 'Closed') { return `This wallet is closed${reason}.`; }
  return null;
}

/** Quick top-up amounts scaled to the school's smallest top-up: 5 -> 25 / 50 / 100. */
export function topUpChoices(minTopUp: number): number[] {
  const base = Math.max(minTopUp || 5, 1);
  return [base * 5, base * 10, base * 20];
}

/**
 * Whether a typed top-up is acceptable, and why not. Empty means "no top-up"
 * when `optional`, else it asks for an amount. `max` is the most a wallet may
 * hold, so what can be added is max minus the current balance. A limit of 0
 * means the school set none.
 */
export function topUpProblem(
  raw: string,
  balance: number,
  min: number,
  max: number,
  currency: string,
  optional = true,
): string | null {
  if (raw.trim() === '') { return optional ? null : 'Enter an amount above zero.'; }
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) {
    return optional ? 'Enter an amount above zero, or leave it empty.' : 'Enter an amount above zero.';
  }
  if (min > 0 && n < min) { return `The smallest top-up is ${walletMoney(min, currency)}.`; }
  if (max > 0 && balance + n > max) {
    return `Add at most ${walletMoney(Math.max(0, max - balance), currency)} - a wallet holds up to ${walletMoney(max, currency)}.`;
  }
  return null;
}

const DAY = 24 * 60 * 60 * 1000;

/** Ticked to begin with: wallet bills, overdue fees, and fees due within a month. */
export function tickedByDefault(item: PayItem, now = Date.now()): boolean {
  return item.kind === 'WALLET_BILL'
    || item.overdue
    || (!!item.dueDate && (parseBackendDateOnly(item.dueDate)?.getTime() ?? Infinity) - now <= 31 * DAY);
}

export const firstName = (name: string) => name.split(/\s+/)[0] ?? name;

export interface Basket {
  fees: number;
  feeCount: number;
  bills: { name: string; amount: number }[];
  topUps: number;
  topUpCount: number;
  total: number;
  problems: number;
}

/** What one Pay & top up payment would cover: ticked items plus every valid top-up. */
export function payBasket(
  data: PayAndTopUp | null,
  ticked: Record<string, boolean>,
  topUps: Record<string, string>,
): Basket {
  const empty: Basket = { fees: 0, feeCount: 0, bills: [], topUps: 0, topUpCount: 0, total: 0, problems: 0 };
  if (!data) { return empty; }
  let fees = 0, feeCount = 0, top = 0, topCount = 0, problems = 0;
  const bills: { name: string; amount: number }[] = [];
  for (const c of data.children) {
    for (const i of c.items) {
      if (!ticked[i.invoiceId]) { continue; }
      if (i.kind === 'WALLET_BILL') { bills.push({ name: firstName(c.name), amount: i.amountDue }); }
      else { fees += i.amountDue; feeCount++; }
    }
    const raw = topUps[c.studentId] ?? '';
    if (topUpProblem(raw, c.walletBalance, data.minTopUp, data.maxBalance, data.currency)) { problems++; continue; }
    const n = Number(raw);
    if (raw.trim() !== '' && n > 0) { top += n; topCount++; }
  }
  const billTotal = bills.reduce((a, b) => a + b.amount, 0);
  return {
    fees, feeCount, bills, topUps: top, topUpCount: topCount, problems,
    total: Math.round((fees + billTotal + top) * 100) / 100,
  };
}

/** The body for POST /wallet/my/pay/start: ticked invoices and each child's top-up. */
export function payStartBody(
  data: PayAndTopUp,
  ticked: Record<string, boolean>,
  topUps: Record<string, string>,
): { invoiceIds: string[]; topUps: { walletId: string; amount: number }[] } {
  const known = new Set(data.children.flatMap(c => c.items.map(i => i.invoiceId)));
  return {
    invoiceIds: Object.entries(ticked).filter(([id, on]) => on && known.has(id)).map(([id]) => id),
    topUps: data.children
      .filter((c: PayChild) => c.walletId && (topUps[c.studentId] ?? '').trim() !== '' && Number(topUps[c.studentId]) > 0)
      .map(c => ({ walletId: c.walletId as string, amount: Number(topUps[c.studentId]) })),
  };
}

/** "16 Oct 2026, 14:05" - a statement line's time. */
export function stamp(iso: string): string {
  // parseBackendDate: a stored-UTC instant without a Z must not read as local time.
  const d = parseBackendDate(iso);
  if (!d) { return iso; }
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${hh}:${mm}`;
}

export interface EntryDetail { label: string; value: string }

/**
 * Everything about one statement line, for the popup a parent opens by tapping it (bug, 27 Oct
 * 2026: the list cuts a long line short and could not be opened). A canteen line's description
 * ("Canteen Purchase · ORD-067 — 5 × coffee, 2 × tea") is split into what, which order and the
 * items, one per line. Empty fields are left out.
 */
export function entryDetails(entry: WalletEntry, currency: string): EntryDetail[] {
  const incoming = entry.direction === 'CR';
  const money = (n: number) => (entry.pocket === 'POINTS' ? `${n} pts` : walletMoney(n, currency));
  const rows: EntryDetail[] = [
    { label: 'Type', value: `${walletKindLabel(entry.kind)}${entry.pocket === 'POINTS' ? ' · points' : ''}` },
    { label: 'Amount', value: `${incoming ? '+' : '-'}${money(entry.amount)}` },
    { label: 'Date and time', value: stamp(entry.createdAt) },
  ];

  const description = entry.description?.trim() ?? '';
  const [head, items] = description.split(' — ');
  const [what, order] = (head ?? '').split(' · ');
  const isOrder = !!order && /^[A-Z]{2,}-[\w-]+$/.test(order.trim());
  if (items !== undefined && isOrder) {
    rows.push({ label: 'For', value: what.trim() });
    rows.push({ label: 'Order', value: order.trim() });
    rows.push({ label: 'Items', value: items.split(/,\s*/).map(s => s.trim()).filter(Boolean).join('\n') });
  } else if (description) {
    rows.push({ label: 'Details', value: description });
  }
  const ref = entry.sourceRef?.trim();
  if (ref && !description.includes(ref)) rows.push({ label: 'Reference', value: ref });
  if (entry.reason?.trim()) rows.push({ label: 'Reason', value: entry.reason.trim() });
  if (entry.performedByName?.trim()) rows.push({ label: 'By', value: entry.performedByName.trim() });
  rows.push({ label: 'Balance after', value: money(entry.balanceAfter) });
  return rows;
}

/**
 * The wallet the Wallet screen shows: the child chosen at the top of the app (owner, 2 Nov 2026 -
 * like the canteen, the screen had its own child chips too, so it could show a different child
 * from the header). Null when that child has no wallet, so the screen says so rather than showing
 * a brother's or sister's. With no child chosen, the first wallet.
 */
export function walletForChild(wallets: Wallet[], studentId?: string | null): Wallet | null {
  if (!studentId) { return wallets[0] ?? null; }
  return wallets.find(w => w.ownerType === 'STUDENT' && w.ownerId.toLowerCase() === studentId.toLowerCase()) ?? null;
}

/** What a family owes right now: fees plus wallet bills, across every child (owner, 16 Oct 2026). */
export interface PendingSummary { total: number; fees: number; bills: number; overdue: number; currency: string }

/** The same sums the web parent dashboard's "Amount pending" banner shows. */
export function pendingSummary(pay: PayAndTopUp | null | undefined): PendingSummary {
  const items = (pay?.children ?? []).flatMap(c => c.items ?? []);
  const sum = (kind?: string) =>
    Math.round(items.filter(i => !kind || i.kind === kind).reduce((n, i) => n + (i.amountDue || 0), 0) * 100) / 100;
  return {
    total: sum(),
    fees: sum('FEE'),
    bills: sum('WALLET_BILL'),
    overdue: items.filter(i => i.overdue).length,
    currency: pay?.currency || 'USD',
  };
}

/** "fees KYD 120.00 · wallet bills KYD 4.50 · 1 overdue" - the banner's second line. */
export function pendingBreakdown(p: PendingSummary): string {
  // The title already shows the total, so a fees-only figure would just repeat it.
  const both = p.fees > 0 && p.bills > 0;
  return [
    both ? `fees ${walletMoney(p.fees, p.currency)}` : null,
    p.bills > 0 ? `wallet bills ${walletMoney(p.bills, p.currency)}` : null,
    p.overdue > 0 ? `${p.overdue} ${p.overdue === 1 ? 'payment' : 'payments'} overdue — pay now` : null,
  ].filter(Boolean).join(' · ');
}
